import { Search } from "lucide-react";

import useTranslations from "@/shared/lang/useTranslations";
import { groupByDay } from "@/shared/utils/groupByDay";

import type { RecentTransaction } from "../types/wallets-api.types";
import { WalletMovementRow } from "./WalletMovementRow";

interface WalletMovementsListProps {
  transactions: RecentTransaction[];
  isLoading: boolean;
  hasFiltersActive: boolean;
  flagCode?: string | null;
  onSelect: (tx: RecentTransaction) => void;
}

const SkeletonRow = () => (
  <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3.5 border-b border-border px-4 py-3 last:border-b-0 sm:grid-cols-[auto_1fr_auto_auto]">
    <div className="h-8 w-8 animate-pulse rounded-md bg-muted" />
    <div className="flex-1 space-y-1.5">
      <div className="h-3 w-3/5 animate-pulse rounded bg-muted" />
      <div className="h-2.5 w-2/5 animate-pulse rounded bg-muted" />
    </div>
    <div className="hidden h-3 w-10 animate-pulse rounded bg-muted sm:block" />
    <div className="h-4 w-14 animate-pulse rounded bg-muted" />
  </div>
);

export const WalletMovementsList = ({
  transactions,
  isLoading,
  hasFiltersActive,
  flagCode,
  onSelect,
}: WalletMovementsListProps) => {
  const { t, activeLanguage } = useTranslations("wallets");

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {Array.from({ length: 5 }).map((_, i) => (
          <SkeletonRow key={i} />
        ))}
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-border bg-card p-12 text-center">
        <Search size={22} className="text-muted-foreground" />
        <p className="text-sm text-foreground">
          {hasFiltersActive ? t("movements_empty_no_results") : t("movements_empty_no_data")}
        </p>
      </div>
    );
  }

  const groups = groupByDay(transactions, {
    getTimestamp: (tx) => tx.ts,
    todayLabel: t("movements_group_today"),
    yesterdayLabel: t("movements_group_yesterday"),
    formatDate: (isoDate) => {
      // The key is a LOCAL `YYYY-MM-DD`; new Date(isoDate) would parse it as UTC midnight and shift the
      // label a day back for negative-offset timezones, so build the date from its parts instead.
      const [year, month, day] = isoDate.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      if (Number.isNaN(date.getTime())) return isoDate;
      return new Intl.DateTimeFormat(activeLanguage, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(date);
    },
  });

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      {groups.map((group, idx) => (
        <div key={group.key}>
          <div
            className={`flex items-center justify-between border-b border-border bg-muted/40 px-4 py-2.5 ${
              idx > 0 ? "border-t" : ""
            }`}
          >
            <span className="font-mono text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
              {group.label || "—"}
            </span>
            <span className="font-mono text-[10.5px] text-muted-foreground">
              {group.items.length}{" "}
              {group.items.length === 1
                ? t("movements_stat_movement_singular")
                : t("movements_stat_movement_plural")}
            </span>
          </div>
          {group.items.map((tx) => (
            <WalletMovementRow key={tx.id} transaction={tx} flagCode={flagCode ?? null} onClick={onSelect} />
          ))}
        </div>
      ))}
    </div>
  );
};
